import { useRef, useState } from "react";
import EventIcon from '@mui/icons-material/Event';
import { Button, Menu, MenuItem, ListItemText, ListSubheader, Tooltip, MenuItemProps } from "@mui/material";

import { CalendarPopup } from "~/components/common/calendar/CalendarPopup";
import AddShift from "~/components/command/AddShift";
import AddCalendarBlock from "~/components/command/AddCalendarBlock";
import { MENU_TRANSITION_DURATION_MS } from "~/components/command/OptimizeButton";

import { CalendarBlockBlockTypeEnum, CommandIndexProvidersQuery } from "~/graphql/__generated__/graphql";
import PermissionOrHide from "../common/PermissionOrHide";
import { Permission } from "~/hooks/permissions";

type AddToCalendarMenuProps = {
  providers: CommandIndexProvidersQuery["commandIndexProviders"]["providers"];
  date: Date;
};

type PopupKind = "" | "shift" | `${CalendarBlockBlockTypeEnum}`;

const AddToCalendarMenu = ({ providers, date }: AddToCalendarMenuProps) => {
  const [openPopup, setOpenPopup] = useState<PopupKind>("");
  const [menuOpen, setMenuOpen] = useState(false);
  const anchorRef = useRef<HTMLButtonElement | null>(null);

  const closePopup = () => setOpenPopup("");

  const handleMenuClick = (popup: PopupKind) => {
    setOpenPopup(popup);
    setMenuOpen(false);
  };

  return (
    <>
      <Button
        ref={anchorRef}
        onClick={() => {
          setMenuOpen(true);
          closePopup();
        }}
        data-testid="add-to-calendar-button"
        variant="outlined"
        endIcon={<EventIcon />}
      >
        Add
      </Button>

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        transitionDuration={MENU_TRANSITION_DURATION_MS}
        slotProps={{
          list: {
            "aria-labelledby": "add-to-calendar-button",
          },
        }}
      >
        <PermissionOrHide permission={Permission.manageProviderShifts}>
          <MenuItem onClick={() => handleMenuClick("shift")}>
            <ListItemText>Shift</ListItemText>
          </MenuItem>
        </PermissionOrHide>

        <PermissionOrHide permission={Permission.manageCalendarBlocks}>
          <ListSubheader sx={{ minWidth: 170 }}>Block time for</ListSubheader>
          <BlockTypes onClick={(type) => handleMenuClick(type)} />
        </PermissionOrHide>
      </Menu>

      <CalendarPopup
        isOpen={!!openPopup}
        visitAnchor={anchorRef.current}
        onClose={closePopup}
      >
        {openPopup === "shift" && (
          <AddShift
            providers={providers}
            date={date}
            key={`add-shift-${providers.length}-${date.toISOString()}`}
            onClose={closePopup}
          />
        )}
        {openPopup === "break" && (
          <AddCalendarBlock date={date} onClose={closePopup} blockType={CalendarBlockBlockTypeEnum.Break} />
        )}
        {openPopup === "meeting" && (
          <AddCalendarBlock date={date} onClose={closePopup} blockType={CalendarBlockBlockTypeEnum.Meeting} />
        )}
        {openPopup === "facility" && (
          <AddCalendarBlock date={date} onClose={closePopup} blockType={CalendarBlockBlockTypeEnum.Facility} />
        )}
        {openPopup === "visit_type" && (
          <AddCalendarBlock date={date} onClose={closePopup} blockType={CalendarBlockBlockTypeEnum.VisitType} />
        )}
      </CalendarPopup>
    </>
  );
};

export default AddToCalendarMenu;

export const BlockTypes = ({ onClick, sx }: { onClick: (type: CalendarBlockBlockTypeEnum) => void, sx?: MenuItemProps["sx"] }) => {
  return <>
    <Tooltip
      title="Add a lunch break or other off-time. Examples: Lunch 12–12:30; Personal break"
      arrow
      placement="right"
    >
      <MenuItem sx={{ pl: 4, ...sx }} onClick={() => onClick(CalendarBlockBlockTypeEnum.Break)}>
        <ListItemText>Break</ListItemText>
      </MenuItem>
    </Tooltip>

    <Tooltip
      title="Add a meeting at a specific address. Examples: Team sync at Clinic HQ; Training at 123 Main St"
      arrow
      placement="right"
    >
      <MenuItem sx={{ pl: 4, ...sx }} onClick={() => onClick(CalendarBlockBlockTypeEnum.Meeting)}>
        <ListItemText>Meeting</ListItemText>
      </MenuItem>
    </Tooltip>

    <Tooltip
      title="Reserve time for visits at a specific facility"
      arrow
      placement="right"
    >
      <MenuItem sx={{ pl: 4, ...sx }} onClick={() => onClick(CalendarBlockBlockTypeEnum.Facility)}>
        <ListItemText>Facility Visits</ListItemText>
      </MenuItem>
    </Tooltip>

    <Tooltip
      title="Reserve time for specific visit types"
      arrow
      placement="right"
    >
      <MenuItem sx={{ pl: 4, ...sx }} onClick={() => onClick(CalendarBlockBlockTypeEnum.VisitType)}>
        <ListItemText>Visit Types</ListItemText>
      </MenuItem>
    </Tooltip>
  </>;
}
